import { useCreateOnboardingRun, useOnboardingRuns } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  EmptyState,
  ErrorState,
  Input,
  Label,
  LoadingState,
  PageHeader,
} from '@hostesa/ui';
import { useState } from 'react';
import { useHotelContext } from '../../lib/hotel-context';
import { namespace } from './i18n';
import { OnboardingWizard } from './onboarding-wizard';

/**
 * OnboardingReviewPage — the website onboarding review wizard. Lists the
 * hotel's onboarding runs, lets the manager start a new run from a URL, and
 * drives the active run through Connect → Review → Activate.
 *
 * This single component backs both the standalone `/onboarding` route and the
 * hotel-scoped onboarding view; the coordinator wires the routes and provides
 * the resolved hotel context.
 */
export function OnboardingReviewPage() {
  const { t } = useT(namespace);
  const hotel = useHotelContext();
  const { tenantId, hotelId } = hotel;

  const runsQuery = useOnboardingRuns(tenantId, hotelId);
  const createRun = useCreateOnboardingRun(tenantId, hotelId);

  const [url, setUrl] = useState('');
  const [ownerConfirmed, setOwnerConfirmed] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const runs = runsQuery.data ?? [];
  const activeRun = selectedRunId ? runs.find((r) => r.id === selectedRunId) : runs[0];

  function startRun() {
    createRun.mutate(
      { url },
      {
        onSuccess: (run) => {
          setSelectedRunId(run.id);
          setUrl('');
          setOwnerConfirmed(false);
        },
      },
    );
  }

  return (
    <div data-testid="onboarding-page" className="flex flex-col gap-6">
      <PageHeader
        data-testid="onboarding-page-header"
        eyebrow={t('eyebrow')}
        title={t('pageTitle')}
        description={t('pageDescription')}
      />

      {/* Start a new run */}
      <form
        data-testid="onboarding-connect-form"
        className="flex flex-col gap-3 rounded-lg border border-[var(--color-warm-stone-soft)] p-4"
        onSubmit={(e) => {
          e.preventDefault();
          startRun();
        }}
      >
        <div className="flex flex-col gap-1">
          <Label htmlFor="onboarding-url">{t('connect.urlLabel')}</Label>
          <Input
            id="onboarding-url"
            type="url"
            data-testid="onboarding-url-input"
            placeholder={t('connect.urlPlaceholder')}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-[var(--color-charcoal)]">
          <input
            type="checkbox"
            data-testid="onboarding-owner-checkbox"
            checked={ownerConfirmed}
            onChange={(e) => setOwnerConfirmed(e.target.checked)}
          />
          {t('connect.ownerConfirm')}
        </label>
        <div>
          <Button
            type="submit"
            data-testid="onboarding-start-btn"
            disabled={createRun.isPending || url.length === 0 || !ownerConfirmed}
          >
            {createRun.isPending ? t('connect.starting') : t('connect.start')}
          </Button>
        </div>
      </form>

      {/* Runs + active wizard */}
      {runsQuery.isLoading && (
        <LoadingState data-testid="onboarding-runs-loading" title={t('states.loading')} />
      )}
      {runsQuery.isError && (
        <ErrorState
          data-testid="onboarding-runs-error"
          title={t('states.errorTitle')}
          description={t('states.errorDescription')}
          action={
            <Button
              variant="secondary"
              data-testid="onboarding-runs-retry-btn"
              onClick={() => void runsQuery.refetch()}
            >
              {t('states.retry')}
            </Button>
          }
        />
      )}
      {!runsQuery.isLoading && !runsQuery.isError && runs.length === 0 && (
        <EmptyState
          data-testid="onboarding-runs-empty"
          title={t('runs.title')}
          description={t('runs.none')}
        />
      )}

      {runs.length > 1 && (
        <div data-testid="onboarding-run-switcher" className="flex flex-wrap gap-2">
          {runs.map((run) => (
            <Button
              key={run.id}
              size="sm"
              variant={run.id === activeRun?.id ? 'primary' : 'secondary'}
              data-testid={`onboarding-run-select-${run.id}`}
              onClick={() => setSelectedRunId(run.id)}
            >
              {t(`runStatus.${run.status}`)}
            </Button>
          ))}
        </div>
      )}

      {activeRun && <OnboardingWizard tenantId={tenantId} hotelId={hotelId} run={activeRun} />}
    </div>
  );
}
